import { useState } from 'react'
import type { GymPlan } from './types'

function isValidPercent(percent: string): boolean {
  const pct = Number(percent)
  return !!percent && Number.isFinite(pct) && pct > 0 && pct <= 100
}

// A discount only helps staff fill in Price Paid. The price is what gets saved,
// so invoices and statistics never need to know a percentage was involved.
export function discountedPrice(price: number, percent: string): number {
  if (!isValidPercent(percent)) return price
  return Math.round(price * (100 - Number(percent))) / 100
}

interface UsePlanDiscountOptions {
  selectedPlan: GymPlan | undefined
  setPricePaid: (value: string) => void
  /** What Price Paid goes back to once the discount is taken off. Defaults to
   *  the plan's list price; editing a plan passes what the member already paid. */
  restorePrice?: (plan: GymPlan) => number
}

export interface PlanDiscount {
  hasDiscount: boolean
  discountPercent: string
  /** The plan's list price with the current percentage applied. */
  priceFor: (plan: GymPlan) => number
  toggle: (on: boolean) => void
  changePercent: (value: string) => void
  /** Tick the discount at `percent` and price `plan` with it. */
  apply: (plan: GymPlan, percent: number) => void
  reset: () => void
}

export function usePlanDiscount({
  selectedPlan,
  setPricePaid,
  restorePrice = (plan) => plan.price,
}: UsePlanDiscountOptions): PlanDiscount {
  const [hasDiscount, setHasDiscount] = useState(false)
  const [discountPercent, setDiscountPercent] = useState('')

  function priceFor(plan: GymPlan) {
    return discountedPrice(plan.price, hasDiscount ? discountPercent : '')
  }

  // Ticking or unticking with no percentage typed leaves Price Paid alone, so a
  // stray click never overwrites what staff entered or what the member paid.
  function toggle(on: boolean) {
    setHasDiscount(on)
    if (!selectedPlan || !isValidPercent(discountPercent)) return
    const price = on
      ? discountedPrice(selectedPlan.price, discountPercent)
      : restorePrice(selectedPlan)
    setPricePaid(price.toFixed(2))
  }

  function changePercent(value: string) {
    setDiscountPercent(value)
    if (!selectedPlan) return
    const price = isValidPercent(value)
      ? discountedPrice(selectedPlan.price, value)
      : restorePrice(selectedPlan)
    setPricePaid(price.toFixed(2))
  }

  function apply(plan: GymPlan, percent: number) {
    setHasDiscount(true)
    setDiscountPercent(String(percent))
    setPricePaid(discountedPrice(plan.price, String(percent)).toFixed(2))
  }

  function reset() {
    setHasDiscount(false)
    setDiscountPercent('')
  }

  return { hasDiscount, discountPercent, priceFor, toggle, changePercent, apply, reset }
}
